import { useState } from "react";
import { MAX_PACKAGES, MIN_PACKAGES } from "@shared/constants";
import { Button } from "@client/components/Button";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { Panel } from "@client/components/Panel";
import { PackageCard } from "@client/components/editor/PackageCard";
import type { DraftAction, EditorModel } from "@client/lib/scenarioDraft";

/**
 * The packages, as cards.
 *
 * Deleting one is the only destructive edit in the editor that reaches beyond
 * the thing being deleted — it empties that package's column in every year's mix
 * and unpicks it from any line item restricted to it — so it is the only one
 * that asks first, and the question names exactly what goes.
 */

interface Props {
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
  /** Inside the rail's dialog, which carries the title and the card chrome. */
  bare?: boolean;
}

export function PackagesPanel({ model, dispatch, disabled, bare = false }: Props) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const target = model.packages.find((pkg) => pkg.id === confirming) ?? null;

  const allocated = target
    ? model.years.reduce((sum, year) => sum + (year.mix[target.id] ?? 0), 0)
    : 0;
  const restricted = target
    ? model.lineItems.filter((item) => item.packageIds.includes(target.id))
    : [];

  return (
    <>
      <Panel
        bare={bare}
        title={bare ? undefined : "Packages"}
        description={bare ? undefined : "What you sell, and what each tier costs to deliver."}
        actions={
          <Button
            size="sm"
            disabled={disabled || model.packages.length >= MAX_PACKAGES}
            title={
              model.packages.length >= MAX_PACKAGES
                ? `A scenario holds at most ${MAX_PACKAGES} packages.`
                : undefined
            }
            onClick={() => dispatch({ type: "addPackage" })}
          >
            Add package
          </Button>
        }
      >
        <ul className="flex flex-col gap-3">
          {model.packages.map((pkg, index) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              index={index}
              count={model.packages.length}
              currencyCode={model.currencyCode}
              disabled={disabled}
              canRemove={model.packages.length > MIN_PACKAGES}
              onPatch={(patch) => dispatch({ type: "patchPackage", id: pkg.id, patch })}
              onMove={(delta) => dispatch({ type: "movePackage", id: pkg.id, delta })}
              onRemove={() => setConfirming(pkg.id)}
            />
          ))}
        </ul>
      </Panel>

      <ConfirmDialog
        open={target !== null}
        title={`Remove “${target?.name ?? ""}”?`}
        body={
          <div className="flex flex-col gap-2">
            <p>Removing this package also clears everything that pointed at it:</p>
            <ul className="list-disc pl-5">
              <li>
                {allocated === 0
                  ? "No clients are assigned to it in any year."
                  : `${allocated} client${allocated === 1 ? "" : "s"} assigned to it across the horizon become unassigned and earn nothing until you re-split them.`}
              </li>
              <li>
                {restricted.length === 0
                  ? "No cost or revenue line is restricted to it."
                  : `${restricted.length} line${restricted.length === 1 ? "" : "s"} restricted to it (${restricted
                      .map((item) => item.name || "unnamed")
                      .join(", ")}) go back to counting the whole client base.`}
              </li>
            </ul>
            <p>Nothing is saved until you save the scenario.</p>
          </div>
        }
        confirmLabel="Remove package"
        tone="danger"
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (target) dispatch({ type: "removePackage", id: target.id });
          setConfirming(null);
        }}
      />
    </>
  );
}
